import React, { useState, useMemo } from 'react';
import Select, { components } from 'react-select';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down-small.svg';
import { Svg } from '@/shared/ui/Svg';
import { FieldWrapper } from '../FieldWrapper';
import { BaseFieldProps } from '../BaseField';
import { Textfield } from '../Textfield';
import styles from './SelectField.module.scss';

// Регистрируем английский язык для стран
countries.registerLocale(enLocale);
export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps extends BaseFieldProps {
  options?: SelectOption[];
  onChange?: (value: string) => void;
  value?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  isCountrySelect?: boolean;
  enableOther?: boolean;
  otherMatches?: string | ((opt: SelectOption | null) => boolean);
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  otherPlaceholder?: string;
  otherError?: string;
  readOnly?: boolean;
}

const createCustomDropdownIndicator =
  (error: string | undefined, isHovered: boolean, disabled?: boolean, readOnly?: boolean) =>
  (props: any) => {
    const { isFocused } = props;

    const getStrokeColor = () => {
      if (disabled || readOnly) return '#9ca3af'; // gray-400 для disabled/readOnly
      if (isHovered || isFocused) return '#3b3b3b';
      if (error) return '#FDA29B';
      return '#000000';
    };

    return (
      <components.DropdownIndicator {...props}>
        <Svg
          Icon={ChevronDownIcon}
          className="w-5 h-5"
          style={{
            stroke: getStrokeColor(),
          }}
        />
      </components.DropdownIndicator>
    );
  };

export const SelectField = (props: SelectFieldProps) => {
  const {
    label,
    placeholder = 'Select',
    error,
    options = [],
    showError = false,
    name,
    labelClassName,
    disabled,
    onChange,
    value,
    isMulti = false,
    isSearchable = false,
    isClearable = false,
    menuPlacement = 'auto',
    isCountrySelect = false,
    enableOther = false,
    otherMatches = 'other',
    otherValue = '',
    onOtherChange,
    otherPlaceholder = 'Please specify',
    otherError,
    readOnly = false,
  } = props;

  const [isHovered, setIsHovered] = useState(false);

  const countryOptions = useMemo(() => {
    if (!isCountrySelect) return [];
    const countryNames = countries.getNames('en', { select: 'official' });
    return Object.entries(countryNames)
      .map(([code, name]) => ({
        value: code,
        label: name as string,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isCountrySelect]);

  // Используем countryOptions если isCountrySelect = true, иначе options
  const finalOptions = isCountrySelect ? countryOptions : options;

  // Находим объект опции по строковому значению
  const selectedOption = value
    ? finalOptions.find((option: SelectOption) => option.value === value)
    : null;

  // Определяем, выбрано ли значение 'Other'
  const isOtherSelected =
    enableOther &&
    (typeof otherMatches === 'function'
      ? otherMatches(selectedOption || null)
      : (selectedOption?.value || '').toLowerCase() === otherMatches.toLowerCase());

  const handleSelectChange = (selectedOption: any) => {
    const newValue = selectedOption?.value || '';
    onChange?.(newValue);

    const isNewValueOther =
      enableOther &&
      (typeof otherMatches === 'function'
        ? otherMatches(selectedOption)
        : newValue.toLowerCase() === otherMatches.toLowerCase());

    if (!isNewValueOther && otherValue) {
      onOtherChange?.('');
    }
  };

  return (
    <FieldWrapper
      label={label}
      error={error}
      showError={showError}
      fieldId={name}
      labelClassName={labelClassName}
      labelAsParagraph
    >
      <div
        className={`relative ${disabled ? 'cursor-not-allowed' : readOnly ? 'cursor-default' : ''}`}
        onMouseEnter={() => !readOnly && setIsHovered(true)}
        onMouseLeave={() => !readOnly && setIsHovered(false)}
      >
        <Select
          instanceId={name}
          name={name}
          // Подпись поля выводится параграфом, а не <label>, поэтому имя полю даём явно
          aria-label={label}
          aria-invalid={showError && error ? true : undefined}
          aria-describedby={showError && error ? `${name}-error` : undefined}
          options={finalOptions}
          value={selectedOption}
          onChange={readOnly ? undefined : handleSelectChange}
          placeholder={placeholder}
          isMulti={isMulti}
          isSearchable={readOnly ? false : isSearchable}
          isClearable={readOnly ? false : isClearable}
          isDisabled={disabled || readOnly}
          menuPlacement={menuPlacement}
          className={styles.reactSelectContainer}
          classNamePrefix="react-select"
          components={{
            DropdownIndicator: readOnly
              ? () => null
              : createCustomDropdownIndicator(error, isHovered, disabled, readOnly),
          }}
          styles={{
            control: (base, state) => {
              const getBorderColor = () => {
                if (error && !readOnly && !disabled) return '#FDA29B';
                if (state.isFocused && !readOnly && !disabled) return '#3b3b3b';
                return 'var(--color-grey-border)';
              };

              const getBackgroundColor = () => {
                if (disabled || readOnly) return '#f9fafb'; // gray-50
                return '#ffffff';
              };

              // react-select уводит фокус на служебный input шириной 1px, и общее кольцо
              // приложения на нём не видно. Рисуем его на контроле, который и выглядит полем.
              // Инлайновые стили самого react-select перебить из таблицы стилей нечем.
              const isFocusRingVisible = state.isFocused && !readOnly && !disabled;

              return {
                ...base,
                minHeight: '48px',
                height: '48px',
                borderRadius: '8px',
                border: `1px solid ${getBorderColor()}`,
                boxShadow: isFocusRingVisible ? '0 0 0 2px var(--focus-ring-gap)' : 'none',
                outline: isFocusRingVisible ? '2px solid var(--focus-ring)' : 'none',
                outlineOffset: '2px',
                backgroundColor: getBackgroundColor(),
                paddingLeft: '16px',
                paddingRight: '6px',
                transition: 'border-color 300ms ease-in-out, background-color 300ms ease-in-out',
                opacity: disabled ? 0.6 : 1,
                cursor: readOnly ? 'default' : disabled ? 'not-allowed' : 'pointer',
              };
            },
            valueContainer: (base) => ({
              ...base,
              padding: '0',
              margin: '0',
            }),
            input: (base) => ({
              ...base,
              margin: '0',
              padding: '0',
              color: '#000000',
              fontSize: '16px',
              lineHeight: '120%',
              letterSpacing: 'normal',
            }),
            placeholder: (base) => ({
              ...base,
              // #8C8C8C давал 3.36:1 на белом — ниже нормы AA. Это grey-secondary из палитры
              color: '#757575',
              fontSize: '16px',
              lineHeight: '120%',
              letterSpacing: 'normal',
              margin: '0',
            }),
            singleValue: (base) => ({
              ...base,
              color: '#000000',
              fontSize: '16px',
              lineHeight: '120%',
              letterSpacing: 'normal',
              margin: '0',
            }),
            multiValue: (base) => ({
              ...base,
              backgroundColor: '#f3f4f6',
              borderRadius: '4px',
            }),
            multiValueLabel: (base) => ({
              ...base,
              color: '#303030',
              fontSize: '14px',
            }),
            multiValueRemove: (base) => ({
              ...base,
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#ef4444',
                color: '#ffffff',
              },
            }),
            indicatorSeparator: () => ({
              display: 'none',
            }),
            dropdownIndicator: (base: any, state: any) => ({
              ...base,
              transition: 'transform 0.3s ease-in-out',
              transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }),
            menu: (base) => ({
              ...base,
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              marginTop: '4px',
            }),
            menuList: (base) => ({
              ...base,
              padding: '4px',
            }),
            option: (base, state) => ({
              ...base,
              borderRadius: '4px',
              padding: '8px 12px',
              fontSize: '16px',
              lineHeight: '1.4',
              transition: 'background-color 0.3s ease-in-out',
              color: state.isSelected ? '#ffffff' : '#303030',
              backgroundColor: state.isSelected
                ? '#630000'
                : state.isFocused
                  ? '#f3f4f6'
                  : 'transparent',
              cursor: readOnly ? 'default' : 'pointer',
              '&:hover': {
                backgroundColor: state.isSelected ? '#630000' : '#f3f4f6',
              },
            }),
          }}
        />
      </div>

      {isOtherSelected && (
        <Textfield
          wrapperClassName="mt-1"
          name={`${name}-other`}
          placeholder={otherPlaceholder}
          value={otherValue}
          onChange={(e) => onOtherChange?.(e.target.value)}
          error={otherError}
        />
      )}
    </FieldWrapper>
  );
};
